import { forwardRef, useState, useCallback, useEffect, useRef } from 'react';
import type {
  FunctionComponent,
  PropsWithoutRef,
  Ref,
  ChangeEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MutableRefObject,
  KeyboardEventHandler
} from 'react';

import type { BaseProps, ForwardProps, NoChildrenProp, OmitStrict } from '../../types';
import Icon, { registerIcon } from '../Icon';
import * as searchIcon from '../Icon/icons/search.icon';
import * as timesIcon from '../Icon/icons/times.icon';
import type { FormControlProps } from '../FormControl';
import {
  useConsolidatedRef,
  useEscape,
  useI18n,
  useOuterEvent,
  useLiveLog,
  useUID,
  useFocusWithin,
  useTheme
} from '../../hooks';
import Flex from '../Flex';
import Popover from '../Popover';
import { sameWidth } from '../Popover/modifiers';
import Menu from '../Menu';
import type { MenuItemProps } from '../Menu';
import Link from '../Link';
import type { LinkProps } from '../Link';
import Text from '../Text';
import { createStringMatcher, getFocusables, createClassName } from '../../utils';
import VisuallyHiddenText from '../VisuallyHiddenText';
import { ThemeOverride } from '../Configuration';
import Button from '../Button';

import {
  StyledClearButton,
  StyledResultsPopover,
  StyledSearchButton,
  StyledSearchInput,
  StyledSearchMenu,
  StyledSearchResultsContainer,
  StyledSearchTextInput,
  StyledSearchFiltersButton,
  StyledSearchIcon
} from './SearchInput.styles';

registerIcon(searchIcon, timesIcon);

export type SearchResult = Pick<MenuItemProps, 'id' | 'primary' | 'secondary' | 'href' | 'onClick'>;

export type RecentSearch = Pick<MenuItemProps, 'id' | 'primary' | 'href' | 'onClick'>;

export type SearchFilter =
  | {
      /** A list of user selectable scopes to filter search with. */
      filters: string[];
      /** The default filter for uncontrolled use. */
      defaultFilters?: string[];
      currentFilters?: never;
    }
  | {
      /** A list of user selectable scopes to filter search with. */
      filters: string[];
      defaultFilters?: never;
      /** The current filter for controlled use. */
      currentFilters?: string[];
    }
  | {
      filters?: never;
      defaultFilters?: never;
      currentFilters?: never;
    };

export type SearchInputProps = NoChildrenProp &
  BaseProps & {
    placeholder?: FormControlProps['placeholder'];
    /**
     * Creates a controlled input and sets the value. Requires an onChange handler to update value.
     * value + onChange is the recommended method per React team.
     */
    value?: FormControlProps['value'];
    /** Called when the SearchInput value changes. */
    onSearchChange?: (value: string) => void;
    /**
     * Called when the SearchButton is clicked or when the Enter key is pressed while SearchInput is in focus.
     * NOTE: this does not trigger an actual form submit event. It simply represents an explicit action by the user to search as opposed to onSearchChange.
     */
    onSearchSubmit?: (value: string) => void;
    /** Called when user changes scope filter. */
    onFiltersChange?: (value: string[]) => void;
    /** Called when user presses a key. */
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    /** A list of matching results presented in the input's dropdown menu. */
    searchResults?: SearchResult[];
    /** A list of recent searches presented in the input's dropdown menu. */
    recentSearches?: RecentSearch[];
    /** Places the menu into an indeterminate loading state. */
    loading?: boolean;
    /** Prop passed to the advanced search page link. */
    advancedSearchLink?: OmitStrict<LinkProps, 'children'>;
    /** Aria label for search input, used by screen readers. */
    searchInputAriaLabel?: string;
    /**
     * Boolean for determining for whether the search results will render in a popover.
     * @default true
     */
    resultsPopover?: boolean;
  } & SearchFilter;

const SearchInput: FunctionComponent<SearchInputProps & ForwardProps> = forwardRef(
  function SearchInput(props: PropsWithoutRef<SearchInputProps>, ref: Ref<HTMLInputElement>) {
    const t = useI18n();

    const {
      value = '',
      defaultFilters,
      currentFilters,
      filters = [],
      onFiltersChange,
      searchResults,
      recentSearches,
      loading: loadingProp,
      advancedSearchLink,
      onSearchChange,
      onSearchSubmit,
      onKeyDown: onKeyDownProp,
      searchInputAriaLabel = t('search'),
      resultsPopover = true,
      className,
      ...restProps
    } = props;

    const id = useUID();
    const listId = useUID();
    const instructionTextId = useUID();
    const { announcePolite } = useLiveLog();
    const bodyRef = useRef<Element>(document.body);
    const inputRef = useConsolidatedRef(ref);
    const filtersRef = useRef<HTMLButtonElement>(null);
    const searchResultsRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();

    const [searchMenuOpen, setSearchMenuOpen] = useState(false);
    const [searchResultItems, setSearchResultItems] = useState<MenuItemProps[] | undefined>(
      undefined
    );
    const [selectedFilters, setSelectedFilters] = useState(defaultFilters ?? []);
    const [downPressed, setDownPressed] = useState(false);

    const hasFilters = filters.length > 1;
    const hasFiltersApplied = selectedFilters.length > 0;
    const hasSearchResults = !!(searchResults && searchResults.length);
    const hasRecentSearches = !!(recentSearches && recentSearches.length);
    const showSearchMenu = (value || recentSearches?.length) && (searchResultItems || loadingProp);
    const useRecents = hasRecentSearches && !value;
    const loading = useRecents ? false : !!loadingProp;
    const searchMenuVisible =
      (resultsPopover && showSearchMenu && searchMenuOpen) || (!resultsPopover && showSearchMenu);

    const handleEscape = useCallback(
      (e: KeyboardEvent) => {
        if (searchMenuOpen) {
          e.preventDefault();
          e.stopPropagation();
          setSearchMenuOpen(false);
          setDownPressed(false);
        }
      },
      [searchMenuOpen]
    );

    useEscape(handleEscape, inputRef, [handleEscape]);

    const onKeyDown = useCallback(
      (e: ReactKeyboardEvent<HTMLInputElement>) => {
        onKeyDownProp?.(e);

        switch (e.key) {
          case 'Enter':
            if (!downPressed) {
              onSearchSubmit?.(value);
            }
            break;
          case 'ArrowDown':
            setDownPressed(true);
            setSearchMenuOpen(true);
            break;
          case 'Tab': {
            const forward = !e.shiftKey;

            if (forward) {
              const popoverFocusables = getFocusables(searchResultsRef);

              if (popoverFocusables.length) {
                e.preventDefault();
                popoverFocusables[0].focus();
              }
            }
            break;
          }
          default:
            break;
        }
      },
      [onSearchSubmit, downPressed, value]
    );

    useOuterEvent('click', [searchResultsRef, inputRef, filtersRef], () => {
      setSearchMenuOpen(false);
    });

    useEffect(() => {
      setDownPressed(false);
    }, [value]);

    useEffect(() => {
      let items: MenuItemProps[] | undefined = [];
      if (hasSearchResults && value) {
        items = searchResults;
      } else if (useRecents) {
        items = recentSearches;
      } else if (!searchResults && (!recentSearches || recentSearches.length === 0)) {
        items = undefined;
      }
      setSearchResultItems(items);
    }, [searchResults, recentSearches, searchMenuOpen, hasSearchResults, hasRecentSearches, value]);

    // Announce results
    useEffect(() => {
      if (!searchMenuVisible || loading) return;

      const timeout = setTimeout(() => {
        announcePolite({
          message: t(
            useRecents ? 'recent_results_count' : 'results_count',
            [searchResultItems?.length ?? 0],
            {
              count: searchResultItems?.length ?? 0
            }
          ),
          type: 'status'
        });
      }, 1000);

      return () => {
        clearTimeout(timeout);
      };
    }, [loading, searchMenuVisible, searchResultItems]);

    const searchMenu = (
      <Menu
        as={StyledSearchMenu}
        listId={listId}
        role='listbox'
        header={
          recentSearches && !value ? (
            <Text variant='secondary'>{t('recent_searches')}</Text>
          ) : undefined
        }
        mode='action'
        items={searchResultItems ?? []}
        loading={loading}
        footer={
          advancedSearchLink ? (
            <Link {...advancedSearchLink}>{t('advanced_search')}</Link>
          ) : undefined
        }
        accent={hasSearchResults ? createStringMatcher(value, undefined, 'gi') : undefined}
        focusControlEl={inputRef.current || undefined}
        pauseDescendantEvaluation={!downPressed}
        aria-label={searchInputAriaLabel || t('search')}
      />
    );

    const focus = useFocusWithin([inputRef, searchResultsRef], focused => {
      if (!focused) {
        setDownPressed(false);
        setSearchMenuOpen(false);
      }
    });

    useEffect(() => {
      if (focus) {
        setSearchMenuOpen(true);
      }
    }, [focus]);

    useEffect(() => {
      if (currentFilters) setSelectedFilters(currentFilters);
    }, [currentFilters]);

    return (
      <>
        <Flex
          container
          as={StyledSearchInput}
          hasFilters={hasFilters}
          showSubmit={!!onSearchSubmit}
          className={createClassName('search-input', className, { loading: !!loadingProp })}
        >
          {hasFilters && (
            <ThemeOverride
              theme={{
                components: {
                  button: {
                    'secondary-color': theme.base.palette['foreground-color'],
                    'secondary-fill-style': 'outline'
                  }
                }
              }}
            >
              <StyledSearchFiltersButton
                ref={filtersRef}
                text={
                  hasFiltersApplied
                    ? `${t('filters')}: ${selectedFilters.join(', ')}`
                    : t('filters')
                }
                variant='simple'
                aria-label={t('search_in_noun', [selectedFilters.join(', ')])}
                icon={hasFiltersApplied ? 'filter-on' : 'filter'}
                iconOnly
                menu={{
                  mode: 'multi-select',
                  items: filters.map(filter => ({
                    primary: filter,
                    id: filter,
                    selected: selectedFilters.includes(filter),
                    onClick: () => {
                      let newSelectedFilters = [];
                      if (selectedFilters.includes(filter)) {
                        newSelectedFilters = selectedFilters.filter(f => f !== filter);
                      } else {
                        newSelectedFilters = [...selectedFilters, filter];
                      }
                      setSelectedFilters(newSelectedFilters);
                      onFiltersChange?.(newSelectedFilters);
                    }
                  })),
                  footer: (
                    <Button
                      variant='link'
                      onClick={() => {
                        setSelectedFilters([]);
                        onFiltersChange?.([]);
                      }}
                    >
                      {t('clear_filters')}
                    </Button>
                  )
                }}
                showSubmit={!!onSearchSubmit}
              />
            </ThemeOverride>
          )}

          {!onSearchSubmit && !hasFiltersApplied && <StyledSearchIcon name='search' />}

          <StyledSearchTextInput
            id={id}
            ref={inputRef}
            aria-label={searchInputAriaLabel}
            {...restProps}
            type='search'
            aria-autocomplete='list'
            aria-controls={!!showSearchMenu && resultsPopover ? listId : undefined}
            aria-haspopup='listbox'
            aria-describedby={searchMenuVisible ? instructionTextId : undefined}
            value={value}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              onSearchChange?.(e.target.value);
              if (e.target.value) setSearchMenuOpen(true);
            }}
            onKeyDown={onKeyDown}
          />

          {value && (
            <StyledClearButton
              icon
              onClick={() => {
                onSearchChange?.('');
                inputRef.current?.focus();
              }}
              variant='simple'
              compact
              label={t('clear')}
              showSubmit={!!onSearchSubmit}
            >
              <Icon name='times' />
            </StyledClearButton>
          )}

          {!onSearchSubmit && hasFiltersApplied && <StyledSearchIcon name='search' />}

          {onSearchSubmit && (
            <StyledSearchButton
              onClick={() => {
                onSearchSubmit?.(value);
              }}
              icon
              label={t('search')}
              variant='simple'
            >
              <Icon name='search' />
            </StyledSearchButton>
          )}
        </Flex>

        {showSearchMenu && resultsPopover && (
          <Popover
            as={StyledResultsPopover}
            target={inputRef.current}
            show={searchMenuOpen}
            placement='bottom-start'
            modifiers={[sameWidth]}
            onKeyDown={(e: KeyboardEvent) => {
              if (!inputRef.current) return;

              if (e.key === 'Tab') {
                const popoverFocusables = getFocusables(searchResultsRef);
                const currentIndex = popoverFocusables.indexOf(e.target as HTMLElement);
                const forward = !e.shiftKey;
                const isLast = currentIndex === popoverFocusables.length - 1;

                if (forward && isLast) {
                  e.preventDefault();
                  const allFocusable = getFocusables(bodyRef as MutableRefObject<HTMLElement>);
                  const nextIndex = allFocusable.indexOf(inputRef.current) + 1;

                  allFocusable[nextIndex]?.focus();
                  setDownPressed(false);
                  setSearchMenuOpen(false);
                } else if (!forward && currentIndex === 0) {
                  e.preventDefault();
                  inputRef.current.focus();
                }
              }
              if (e.key === 'Escape') {
                handleEscape(e);
                inputRef.current.focus();
              }
            }}
            ref={searchResultsRef}
          >
            {searchMenu}
          </Popover>
        )}

        {!resultsPopover && showSearchMenu && (
          <StyledSearchResultsContainer>{searchMenu}</StyledSearchResultsContainer>
        )}

        <VisuallyHiddenText id={instructionTextId}>
          {`${t('search_instructions')} `}
        </VisuallyHiddenText>
      </>
    );
  }
);

export default SearchInput;
